import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { DestroyOptions, Graphics, Sprite } from "pixi.js";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { SoundManager } from "../../core/SoundManager";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";

export class Character extends DisplayObject {
    private character: Spine | null = null;
    private lightCharacter: Spine | null = null;
    private infernCharacter: Spine | null = null;
    private oceanCharacter: Spine | null = null;
    private characterCloud: Sprite | null = null;
    private intendedAnim: string;
    private lastNonIdleTime: number = Date.now();
    private idleCheckTicker?: ReturnType<typeof setInterval>;
    private idleVariants = ["idle"];
    private characterHitMask: Graphics = new Graphics();
    private characterHitArea: { x: number; y: number; w: number; h: number } | null = null;
    /** When set, click cycles only within this ordered list (free game). */
    private characterSwitchPool: Spine[] | null = null;
    private canSwitchCharacter = true;

    constructor(config: IDisplayObject) {
        super(config);
        this.init();
        this.subscribeToEvents();
        this.setCharacterPositionResize(true);
    }

    public override destroy(options?: DestroyOptions): void {
        this.stopIdleCheck();
        this.unsubscribeToEvents();
        this.lightCharacter?.removeAllListeners();
        this.infernCharacter?.removeAllListeners();
        this.oceanCharacter?.removeAllListeners();
        super.destroy(options);
    }

    public animateCharacter(name?: string): void {
        const animations = ["win"];
        const randomIndex = Math.floor(Math.random() * animations.length);
        const winVariant = animations[randomIndex];
        this.intendedAnim = (name) ? name : winVariant;
        this.character?.state.setEmptyAnimation(0, 0);
        this.character?.state.setAnimation(0, `${this.intendedAnim}`, false);
    }

    public speedupCharacterAnim() {
        this.character.state.timeScale = 2;
    }

    public resetSpeedCharacterAnim() {
        this.character.state.timeScale = 1;
    }

    /**
     * Configures which mascots appear in free game based on backend triggerFeatures.
     * - 1 feature: that mascot only (no click switch)
     * - 2 features: 1st triggered mascot shown; click toggles to the 2nd only
     * - 3 features: Zeus (mascot1) enters; click cycles zeus → hades → poseidon
     */
    public configureForTriggeredFeatures(triggerFeatures: string[]): void {
        const features = triggerFeatures ?? [];
        const charForFeature = (feature: string): Spine | null => {
            switch (feature) {
                case "zeus":
                    return this.lightCharacter;
                case "hades":
                    return this.infernCharacter;
                case "poseidon":
                    return this.oceanCharacter;
                default:
                    return null;
            }
        };

        let initialCharacter: Spine;
        let switchPool: Spine[];

        if (features.length <= 0) {
            switchPool = [this.lightCharacter, this.infernCharacter, this.oceanCharacter];
            initialCharacter = this.lightCharacter;
            this.canSwitchCharacter = true;
        } else if (features.length === 1) {
            const only = charForFeature(features[0]);
            if (!only) return;
            switchPool = [only];
            initialCharacter = only;
            this.canSwitchCharacter = false;
        } else if (features.length === 2) {
            switchPool = features
                .map(charForFeature)
                .filter((c): c is Spine => c !== null);
            if (switchPool.length < 2) return;
            initialCharacter = switchPool[0];
            this.canSwitchCharacter = true;
        } else {
            switchPool = [this.lightCharacter, this.infernCharacter, this.oceanCharacter];
            initialCharacter = this.lightCharacter;
            this.canSwitchCharacter = true;
        }

        this.characterSwitchPool = switchPool;
        this.showCharacter(initialCharacter, true);
    }

    private getNextCharacter(current: Spine): Spine | null {
        if (!this.characterSwitchPool || this.characterSwitchPool.length <= 1) {
            return null;
        }

        const currentIndex = this.characterSwitchPool.indexOf(current);
        if (currentIndex === -1) {
            return this.characterSwitchPool.find((c) => c !== current) ?? null;
        }

        return this.characterSwitchPool[(currentIndex + 1) % this.characterSwitchPool.length];
    }

    private detachAllCharacterSpines(): void {
        [this.lightCharacter, this.infernCharacter, this.oceanCharacter].forEach((spine) => {
            if (spine?.parent === this) {
                this.removeChild(spine);
            }
        });
    }

    private showCharacter(nextCharacter: Spine, playEnterAnimation: boolean): void {
        this.detachAllCharacterSpines();
        this.character = nextCharacter;

        if (playEnterAnimation) {
            this.character.visible = false;
            this.addChild(this.character);

            const inListener = {
                start: () => {
                    this.character.visible = true;
                },
                complete: (entry: { animation: { name: string } }) => {
                    if (entry.animation.name !== "in") return;
                    this.character.state.removeListener(inListener);
                    this.character.state.setAnimation(0, "idle", true);
                },
            };

            this.character.state.addListener(inListener);
            if(this.character == this.lightCharacter){
                sound.play(SoundConstants.ZEUS, { volume: 1 });
            } else if(this.character == this.oceanCharacter){
                sound.play(SoundConstants.OCEAN, { volume: 1 });
            } else if(this.character == this.infernCharacter){
                sound.play(SoundConstants.HADES, { volume: 1 });
            }
            this.character.state.setAnimation(0, "in", false);
        } else {
            this.character.visible = true;
            this.addChild(this.character);
            this.character.state.setEmptyAnimation(0, 0);
            this.character.state.setAnimation(0, "idle", true);
        }

        this.addChild(this.characterHitMask);
        if (this.characterCloud && this.characterCloud.parent !== this) {
            this.addChild(this.characterCloud);
        }
        this.positionCharacterCloud();
    }

    private switchToCharacter(newCharacter: Spine): void {
        const oldCharacter = this.character;
        if (!oldCharacter || oldCharacter === newCharacter) return;

        const outListener = {
            complete: (entry: { animation: { name: string } }) => {
                if (entry.animation.name !== "out") return;

                oldCharacter.state.removeListener(outListener);
                this.showCharacter(newCharacter, true);
            },
        };

        oldCharacter.state.addListener(outListener);
        oldCharacter.state.setAnimation(0, "out", false);
    }

    private init(): void {
        // --- BLUE CHARACTER ---
        this.lightCharacter = Spine.from({
            skeleton: "lightCharacterSkel",
            atlas: "lightCharacterAtlas"
        });
        this.lightCharacter.label = "lightCharacter";

        this.lightCharacter.state.addListener({
            start: (entry: any) => {
                if (entry.animation.name === "win" || entry.animation.name === "in") {
                    sound.play(SoundConstants.ZEUS, { volume: 1 });
                }
            },
            complete: (entry: any) => {
                // Keep self-contained; never use this.character
                if (["in", "win"].includes(entry.animation.name)) {
                    this.lightCharacter.state.setAnimation(0, "idle", true);
                }
            }
        });
        this.lightCharacter.position.set(0, 400);
        // --- PINK CHARACTER ---
        this.infernCharacter = Spine.from({
            skeleton: "infernCharacterSkel",
            atlas: "infernCharacterAtlas"
        });
        this.infernCharacter.label = "infernCharacter";

        this.infernCharacter.state.addListener({
            start: (entry: any) => {
                if (entry.animation.name === "win" || entry.animation.name === "in") {
                    sound.play(SoundConstants.HADES, { volume: 1 });
                }
            },
            complete: (entry: any) => {
                if (["in", "win"].includes(entry.animation.name)) {
                    this.infernCharacter.state.setAnimation(0, "idle", true);
                }
            }
        });
        this.infernCharacter.position.set(0, 400);
        // --- GREEN CHARACTER ---
        this.oceanCharacter = Spine.from({
            skeleton: "oceanCharacterSkel",
            atlas: "oceanCharacterAtlas"
        });
        this.oceanCharacter.label = "oceanCharacter";

        this.oceanCharacter.state.addListener({
            start: (entry: any) => {
                if (entry.animation.name === "win" || entry.animation.name === "in") {
                    sound.play(SoundConstants.OCEAN, { volume: 1 });
                }
            },
            complete: (entry: any) => {
                if (["in", "win"].includes(entry.animation.name)) {
                    this.oceanCharacter.state.setAnimation(0, "idle", true);
                }
            }
        });
        this.oceanCharacter.position.set(0, 400);
        // this.character = Spine.from({
        //     skeleton: "characterSkel",
        //     atlas: "characterAtlas"
        // });
        this.character = this.lightCharacter;
        const animations = this.character.skeleton.data.animations.map(anim => anim.name);
        console.log("Available character animations:", animations);

        this.addChild(this.character);
        // // Add listener to return to "idle" after the win animation completes
        //this.character.state.addListener(this.stateListener);

        this.intendedAnim = "idle";
        this.character?.state.setEmptyAnimation(0, 0);
        this.character.state.setAnimation(0, "idle", true);
        this.character.state.timeScale = 1;

        // // Start periodic check
        if (this.idleVariants.length > 1) {
            this.startIdleCheck();
        }
        this.characterHitMask.label = "spine Hitmask"
        this.addChild(this.characterHitMask);
        this.characterHitMask.eventMode = "static";


        this.sortableChildren = true;
        this.characterCloud = Sprite.from("character_cloud");
        this.characterCloud.label = "characterCloud";
        this.characterCloud.anchor.set(0.5, 0.5);
        this.characterCloud.zIndex = 1;
        this.addChild(this.characterCloud);
        this.positionCharacterCloud();

    }

    private positionCharacterCloud(): void {
        if (!this.characterCloud) return;

        this.characterCloud.position.set(0, 450);
        this.characterCloud.scale.set(1, 1);
    }

    private stateListener = {
        start: (entry: any) => {

            if (entry.animation.name !== this.intendedAnim) {
                // console.warn(`Sound skipped: started ${entry.animation.name}, but expected ${this.intendedAnim}`);
                return;
            }
            switch (entry.animation.name) {
                case "laughing": SoundManager.play('laughing');
                    break;
                case "jump": SoundManager.play('jump');
                    break;
                case "action_1": SoundManager.play('full_sword_up');
                    break;
                case "action_2": SoundManager.play('slam_the_ground');
                    break;
                case "idle_2": SoundManager.play('back_scratching');
                    break;
                case "idle_3": SoundManager.play('low_hand_raise');
                    break;
            }

        },

        complete: (entry: any) => {
            if (["idle", "win"].includes(entry.animation.name)) {
                this.intendedAnim = "idle";
                this.character?.state.setEmptyAnimation(0, 0);
                this.character?.state.setAnimation(0, "idle", true);
            }

            // If animation is not idle, update the timestamp
            if (entry.animation.name !== "idle") {
                this.lastNonIdleTime = Date.now();
            }
        }
    }

    private startIdleCheck(): void {
        if (this.idleCheckTicker) return; // prevent duplicate intervals

        const idleCheckInterval: number = 15000; // Check every 500ms
        let idleThreshold: number = 5000 + Math.floor(Math.random() * 10000); // 5 seconds



        this.idleCheckTicker = setInterval(() => {
            const now = Date.now();
            const currentAnim = this.character?.state.getCurrent(0)?.animation.name;

            if (currentAnim === "idle" && (now - this.lastNonIdleTime >= idleThreshold)) {
                // Select a random idle variant
                const randomAnim = this.idleVariants[Math.floor(Math.random() * this.idleVariants.length)];
                this.intendedAnim = randomAnim;
                this.character?.state.setEmptyAnimation(0, 0);
                this.character?.state.setAnimation(0, randomAnim, false);

                // SoundManager.play('man_anim_sword_to_hand', { volume: 0.5 });
                this.lastNonIdleTime = now; // reset timer after playing a variant
                idleThreshold = 5000 + Math.floor(Math.random() * 10000); // reset idleThreshold 10-20 sec - check after that
            }
        }, idleCheckInterval);
    }

    private stopIdleCheck(): void {
        if (this.idleCheckTicker) {
            clearInterval(this.idleCheckTicker);
            this.idleCheckTicker = undefined;
        }
    }

    private subscribeToEvents() {
        this.character.eventMode = "static";
        //this.character.on("pointerdown", this.onCharacterClick);
        this.characterHitMask.on("pointerdown", this.onCharacterClick);
    }

    private unsubscribeToEvents() {
        this.character.state.removeListener(this.stateListener);
        this.character.eventMode = "static";
        //this.character.off("pointerdown", this.onCharacterClick);
        this.characterHitMask.off("pointerdown", this.onCharacterClick);
    }

    //@ts-ignore
    private onCharacterClick = () => {
        if (!this.character || !this.canSwitchCharacter) return;

        const currentTrack = this.character.state.getCurrent(0);
        const currentAnimName = currentTrack ? currentTrack.animation.name : "";
        if (currentAnimName !== "idle") return;

        const oldCharacter = this.character;
        let newCharVariant: Spine | null;

        if (this.characterSwitchPool) {
            newCharVariant = this.getNextCharacter(oldCharacter);
        } else {
            const charVariants = [this.lightCharacter, this.infernCharacter, this.oceanCharacter];
            const availableChars = charVariants.filter((c) => c !== oldCharacter);
            newCharVariant = availableChars[Math.floor(Math.random() * availableChars.length)];
        }

        if (!newCharVariant) return;

        this.switchToCharacter(newCharVariant);
    }

    public setCharacterPositionResize(check: boolean) {
        let config = check ? this.config.landscape : this.config.portrait;
        this.position.set(config.posX, config.posY);
        this.scale.set(config.scale);
    }

    public override setPositionResize(orientation: string): void {
        if (orientation === "LANDSCAPE") {
            this.setPosition(this.config.landscape.posX, this.config.landscape.posY);
            this.setScale(this.config.landscape.scale, this.config.landscape.scale);
            this.characterHitArea = { x: -210, y: -350, w: 400, h: 750 };
        } else {
            this.setPosition(this.config.portrait.posX, this.config.portrait.posY);
            this.setScale(this.config.portrait.scale, this.config.portrait.scale);
            this.characterHitArea = { x: 550, y: -420, w: 350, h: 500 };
        }
        this.characterHitMask?.clear();
        this.characterHitMask?.rect(this.characterHitArea.x, this.characterHitArea.y, this.characterHitArea.w, this.characterHitArea.h)
            .fill({ color: 0xff0000, alpha: 0.001 });
        this.positionCharacterCloud();
    }
}
